import { CircularProgress, makeStyles, Theme } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { Field } from 'formik'
import LiquidSelectAdapter, {
  LiquidSelectAdapterProps,
} from '@src/components/LiquidSelectAdapter/LiquidSelectAdapter'
import { RequestSource } from '@src/types/graphql-types'
import { PartnerAddressesComponent } from '@src/queries/PartnerAddressesQuery.generated'
import { useUserSession } from '@src/utils/useUserSession'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import { formatShipToLabel } from '@utils/searchUtils'
import { useCheckUserCountry } from '@src/utils/useCheckUserCountry'
import countriesMap from '@localization/countries.json'
import { useSoldToAccountsQuery } from '@src/queries/SoldToAccountsQuery.generated'

const useStyles = makeStyles((theme: Theme) => ({
  shipTo: {
    width: '100%',

    [theme.breakpoints.up('sm')]: {
      maxWidth: '312px',
    },
  },
  customer: {
    width: '100%',

    [theme.breakpoints.up('sm')]: {
      width: '50%',
      flexDirection: 'row',
    },
  },
  loadingIcon: {
    alignItems: 'center',
    display: 'flex',
    marginTop: '10px',
    width: '100%',
    justifyContent: 'center',

    [theme.breakpoints.up('sm')]: {
      width: '50%',
    },
  },
}))

const PartnerAddressSearchFields = ({ formikBag }) => {
  const classes = useStyles()
  const intl = useIntl()
  const { userSession } = useUserSession()
  const { isPurchasingAgent, hasUniqueSoldTos, currentUser, userIsLoggedIn } =
    useCurrentUser()
  const isChinaUser = useCheckUserCountry(countriesMap.CN.countryCode)
  const CONTACT_DEFAULT_VALUE = 'NONE'
  const SHIP_TO_DEFAULT_VALUE = 'ALL'

  const { data } = useSoldToAccountsQuery({
    variables: { requestSource: RequestSource.Order },
    // skip: !(isPurchasingAgent || hasUniqueSoldTos),
  })

  const soldToAccounts = data ? data.getSoldToAccounts : []

  return (
    <>
      <Field
        name="soldTo"
        component={(props: LiquidSelectAdapterProps) => (
          <LiquidSelectAdapter
            {...props}
            rootClass={classes.customer}
            onChange={(e) => {
              props.field.onChange(e)
              // Reset the 'shipTo' field when 'soldTo' is modified since new `shipTo`s will populate
              formikBag.setFieldValue('shipToList', SHIP_TO_DEFAULT_VALUE)
            }}
          />
        )}
        label={<FormattedMessage id="CUSTOMER" defaultMessage="Customer" />}
        options={soldToAccounts.map((soldTo) => ({
          label: `${soldTo.accountNumber}-${soldTo.organizationName}`,
          value: soldTo.accountNumber,
        }))}
      />

      {formikBag.values.soldTo && (
        <PartnerAddressesComponent
          context={{ userSession }}
          variables={{
            input: {
              soldTos: [formikBag.values.soldTo],
            },
            requestSource: RequestSource.Order,
          }}
          ssr={false}
        >
          {({ data, error, loading }) => {
            if (loading) {
              return (
                <div className={classes.loadingIcon}>
                  <CircularProgress size={20} />
                </div>
              )
            }

            if (error || !data) {
              return null
            }
            const { getPartnerAddresses: partners } = data

            const soldToPartnersArray: any = currentUser?.soldToPartners || []

            const profileSoldTo =
              userIsLoggedIn &&
              soldToPartnersArray?.filter(
                (partner) => partner.soldTo === formikBag.values.soldTo
              )[0]

            return (
              <Field
                name="shipToList"
                component={(props: LiquidSelectAdapterProps) => (
                  <LiquidSelectAdapter
                    {...props}
                    rootClass={classes.shipTo}
                    onChange={(e) => {
                      props.field.onChange(e)
                      // Reset the 'contact' field when 'shipTo' is modified
                      formikBag.setFieldValue('contact', CONTACT_DEFAULT_VALUE)
                    }}
                  />
                )}
                data-testid="shipTo-input"
                label={
                  <FormattedMessage
                    id="order_search_ship_to"
                    defaultMessage="Ship To"
                  />
                }
                disabled={
                  !isPurchasingAgent && hasUniqueSoldTos && profileSoldTo
                }
                options={[
                  {
                    label: intl.formatMessage({
                      id: 'md_search_all_ship_to',
                      defaultMessage: 'All',
                    }),
                    value: 'ALL',
                  },
                  ...partners.shipTos.map((shipTo) => ({
                    label: formatShipToLabel(shipTo, isChinaUser),
                    value: shipTo.accountNumber,
                  })),
                ]}
              />
            )
          }}
        </PartnerAddressesComponent>
      )}
    </>
  )
}

export default PartnerAddressSearchFields
